import { Bike, Clock, MapPin, Phone, Receipt, StickyNote } from 'lucide-react';
import Drawer from './Drawer';
import Button from './Button';
import StatusBadge from './StatusBadge';
import Avatar from './Avatar';
import { formatCurrency, ORDER_STATUS } from '../data/mockData';
import { formatQty, formatPrice, isSoldByWeight, lineAmount } from '../utils/soldBy';
import { DELIVERY_MODE, PAYMENT_METHOD_LABELS, formatSlotWindow } from '../api/orders';
import styles from './OrderDetailsDrawer.module.css';

export default function OrderDetailsDrawer({
  order, onClose, onConfirm, onCancel, onPack, onAssign, onUnassign, onResend, onViewBill,
}) {
  if (!order) return null;

  return (
    <Drawer
      open={!!order}
      onClose={onClose}
      title={order.id}
      subtitle={new Date(order.placedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
      width={480}
      footer={
        order.channel === 'SUBS' ? (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--color-text-muted)' }}>
            Subscription drop — manage on the Subs Run Sheet, not grocery Delivery.
          </p>
        ) : (
        <>
          {order.status === ORDER_STATUS.RECEIVED && (
            <>
              <Button variant="ghost" onClick={() => { onCancel(order.id); onClose(); }}>Cancel order</Button>
              <Button variant="primary" onClick={() => { onConfirm(order.id); onClose(); }}>Confirm order</Button>
            </>
          )}
          {order.status === ORDER_STATUS.CONFIRMED && (
            <>
              <Button variant="ghost" onClick={() => { onCancel(order.id); onClose(); }}>Cancel order</Button>
              {onResend && (
                <Button variant="outline" icon={Receipt} onClick={() => onResend(order.id)}>Resend invoice</Button>
              )}
              <Button variant="primary" onClick={() => { onPack(order.id); onClose(); }}>Mark as packed</Button>
            </>
          )}
          {order.status === ORDER_STATUS.PACKED && (
            <Button variant="primary" icon={Bike} onClick={() => onAssign(order)}>Assign rider</Button>
          )}
          {/* OFD unassign disabled by design (API: cannot_unassign_in_transit)
          {order.status === ORDER_STATUS.OUT_FOR_DELIVERY && (
            <Button variant="ghost" onClick={() => onUnassign(order.id)}>Unassign rider</Button>
          )}
          */}
        </>
        )
      }
    >
      <div className={styles.statusRow}>
        <StatusBadge status={order.status} />
        {order.channel === 'SUBS' && (
          <span className={styles.scheduleTag}>Subscription delivery</span>
        )}
        {order.channel === 'SUBS' && order.dropDate && (
          <span className={styles.scheduleTag}>Drop {order.dropDate}</span>
        )}
        {order.deliveryMode === DELIVERY_MODE.SCHEDULED && order.channel !== 'SUBS' && (
          <span className={styles.scheduleTag}>
            <Clock size={13} /> {formatSlotWindow(order.slotStart, order.slotEnd) || 'Scheduled'}
          </span>
        )}
        {order.rider && (
          <span className={styles.riderTag}><Bike size={13} /> {order.rider.name}</span>
        )}
      </div>

      <section className={styles.block}>
        <div className={styles.customerHead}>
          <Avatar name={order.customer.name} size={40} />
          <div>
            <p className={styles.customerName}>{order.customer.name || 'Customer'}</p>
            {order.customer.phone ? (
              <p className={styles.customerPhone}><Phone size={11} /> {order.customer.phone}</p>
            ) : order.customer.sub ? (
              <p className={styles.customerPhone}>ID: {order.customer.sub}</p>
            ) : null}
          </div>
        </div>
        {order.address && (
          <p className={styles.address}><MapPin size={13} /> {order.address}</p>
        )}
        {order.notes && (
          <p className={styles.notes}><StickyNote size={13} /> {order.notes}</p>
        )}
        {order.failureReason && (
          <p className={styles.failure}>{order.failureReason}</p>
        )}
      </section>

      <section className={styles.block}>
        <p className={styles.sectionTitle}>Items</p>
        {order.items.length === 0 ? (
          <p className={styles.itemQty}>Item details unavailable.</p>
        ) : (
          <div className={styles.itemsList}>
            {order.items.map((it, i) => (
              <div key={i} className={styles.itemRow}>
                <span className={styles.itemEmoji}>{it.emoji || '🧺'}</span>
                <div className={styles.itemInfo}>
                  <p className={styles.itemName}>{it.name}</p>
                  <p className={styles.itemQty}>{formatQty(it.qty, it)} × {formatPrice(it, formatCurrency)}</p>
                  {order.status === ORDER_STATUS.CONFIRMED && isSoldByWeight(it) && (
                    <p className={styles.itemQty}>Weigh to {formatQty(it.qty, it)}</p>
                  )}
                  {it.returnStatus && (
                    <span style={{ fontSize: '11px', color: '#e11d48', fontWeight: 600 }}>
                      Return/Exchange: {it.returnStatus}
                    </span>
                  )}
                </div>
                <span className={styles.itemTotal}>{formatCurrency(lineAmount(it.price, it.qty))}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className={styles.block}>
        <div className={styles.billRow}>
          <span>Subtotal</span>
          <span>{formatCurrency(order.subtotal)}</span>
        </div>
        {order.discountAmount > 0 && (
          <div className={`${styles.billRow} ${styles.billDiscount}`}>
            <span>Discount{order.couponCode ? ` (${order.couponCode})` : ''}</span>
            <span>−{formatCurrency(order.discountAmount)}</span>
          </div>
        )}
        <div className={styles.billRow}>
          <span>Delivery fee</span>
          <span>{order.deliveryFee === 0 ? 'Free' : formatCurrency(order.deliveryFee)}</span>
        </div>
        <div className={`${styles.billRow} ${styles.billTotal}`}>
          <span>Total</span>
          <span>{formatCurrency(order.total)}</span>
        </div>
        {order.storeCreditUsed > 0 && (
          <>
            <div className={`${styles.billRow} ${styles.billDiscount}`}>
              <span>Store Credit Applied</span>
              <span>−{formatCurrency(order.storeCreditUsed)}</span>
            </div>
            <div className={`${styles.billRow} ${styles.billTotal}`} style={{ marginTop: '4px', fontSize: '15px' }}>
              <span>Final Paid</span>
              <span>{formatCurrency(order.amountPaid)}</span>
            </div>
          </>
        )}
        <div className={styles.paymentTag}>Paid via {PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}</div>
        {onViewBill && (
          <Button variant="outline" icon={Receipt} className={styles.viewBillBtn} onClick={() => onViewBill(order.id)}>
            View / print bill
          </Button>
        )}
      </section>
    </Drawer>
  );
}
